// Wrapper for the Temp AWS API
// This is include API endbpoints from early in the migration work that while they are AWS
//based, they use an intermediate bearer token auth mechanism
// This API is in the progress of being migrated to the new AWS (WebApi) API
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { logApiRequest } from '../utils/logging';
import type { UserSubscriptions } from 'tsconfig/types';

type AwsLegacyApiConfig = {
  urlPrefix: string;
  extraHeaders?: Record<string, string>;
  brandToken?: string;
  userToken?: string;
  enableLogging?: boolean;
};

export const initAwsTempApi = (config: AwsLegacyApiConfig) => {
  async function asyncRequest(requestType: string, url: string, token?: string, data = {}): Promise<any> {
    let requestConfig: AxiosRequestConfig = {},
      response: AxiosResponse | undefined,
      incomingRequestTime: Date | undefined,
      outgoingResponseTime: Date | undefined;

    try {
      incomingRequestTime = new Date();
      const requestUrl = config.urlPrefix + url;
      requestConfig = {
        method: requestType,
        url: requestUrl,
        headers: {
          'content-type': 'application/json',
          ...config.extraHeaders,
        },
        data,
      };

      if (token) {
        requestConfig.headers!.Authorization = `Bearer ${token}`;
      }

      if (requestType === 'DELETE' || (requestType === 'POST' && Object.keys(data).length === 0)) {
        requestConfig.data = '';
      }

      response = await axios.request(requestConfig);
      return response?.data || {};
    } catch (e: any) {
      //rethrow error for caller to handle
      throw e;
    } finally {
      if (config.enableLogging) {
        outgoingResponseTime = new Date();
        //Note: may be better to throw/pass back this info so caller can log (+ add meaningful message)?
        logApiRequest(requestConfig, response, incomingRequestTime, outgoingResponseTime, 'AwsLegacyApi');
      }
    }
  }

  const userPreferencesApiPrefix = '/wine-preferences/user-preferences/';
  const myUserPreferenceApiPrefix = '/wine-preferences/me/user-preferences';

  return {
    BrandPreferences: {
      get: async () => asyncRequest('GET', '/wine-preferences/brand-preferences', config.brandToken),
    },
    UserPreferences: {
      get: async (params: any) => asyncRequest('GET', userPreferencesApiPrefix + params.userId, config.userToken),
      post: async (params: any, data: any) =>
        asyncRequest('POST', userPreferencesApiPrefix + params.userId, config.userToken, data),
      put: async (params: any, data: any) =>
        asyncRequest('PUT', userPreferencesApiPrefix + params.userId, config.userToken, data),
    },
    MyUserPreferences: {
      get: async () => asyncRequest('GET', myUserPreferenceApiPrefix, config.userToken),
      post: async (data: any) => asyncRequest('POST', myUserPreferenceApiPrefix, config.userToken, data),
      put: async (data: any) => asyncRequest('PUT', myUserPreferenceApiPrefix, config.userToken, data),
    },
    Product: {
      get: async (countryBrand: string, itemCode: string) =>
        asyncRequest('GET', `/products/in-brand/${countryBrand}/${itemCode}`),
      compliance: async (stateCode: string, itemCode: string, brandKey: string, complianceType: string) =>
        asyncRequest(
          'GET',
          `/products/in-brand/${brandKey}/compliance/${itemCode}?stateCode=${stateCode}&complianceType=${complianceType}`,
        ),
    },
    Subscription: {
      get: async (): Promise<UserSubscriptions> => asyncRequest('GET', `/subscriptions/me/user-subscriptions`),
      updateAutoRenew: async (subId: number, data: any) =>
        asyncRequest('POST', `/subscriptions/me/user-subscriptions/${subId}`, config?.userToken, data),
      getCancelReason: async (market: string, subscriptionType: string, type: string) =>
        asyncRequest('GET', `/subscriptions/reasons/${market}/${subscriptionType}/${type}`),
      getCancelOffers: async (subId: string, saveType: string) =>
        asyncRequest('GET', `/subscriptions/me/user-subscriptions/${subId}/offers/${saveType}`),
      applyUnlimitedOffer: async (subId: string, saveType: string, data: { offerId: string }) =>
        asyncRequest(
          'POST',
          `/subscriptions/me/user-subscriptions/${subId}/offers/${saveType}`,
          config?.userToken,
          data,
        ),
    },
  };
};
